/**
 * Horizontal bounds where content is not covered by carousel prev/next arrows.
 * Shared by game-zone Snowplow `on_screen` and `TopNavImpressionTracker` (same `on_screen` rule).
 */
export const getDraggableClickableHorizontalBounds = (
    scrollable: HTMLElement
): { clickableLeft: number; clickableRight: number } => {
    const draggableContainer = scrollable.closest('.draggable-container');
    const prevArrow = draggableContainer?.querySelector('.prev-arrow') as HTMLElement | null;
    const nextArrow = draggableContainer?.querySelector('.next-arrow') as HTMLElement | null;
    const { left: scrollableLeft, right: scrollableRight } = scrollable.getBoundingClientRect();

    return {
        clickableLeft: prevArrow ? prevArrow.getBoundingClientRect().right : scrollableLeft,
        clickableRight: nextArrow ? nextArrow.getBoundingClientRect().left : scrollableRight
    };
};

export const isRectIntersectingClickableHorizontalBandWithBounds = (
    rect: DOMRectReadOnly,
    bounds: { clickableLeft: number; clickableRight: number }
): boolean => rect.right > bounds.clickableLeft && rect.left < bounds.clickableRight;

/**
 * Whether `rect` intersects the horizontal strip between carousel arrows (top-nav parity).
 */
export const isRectIntersectingClickableHorizontalBand = (rect: DOMRectReadOnly, scrollable: HTMLElement): boolean => {
    return isRectIntersectingClickableHorizontalBandWithBounds(rect, getDraggableClickableHorizontalBounds(scrollable));
};

const GRID_ZONE_LAYOUT_SELECTOR = '.grid-zone-layout';

const DRAGGABLE_VISIBLE_EDGE_TOLERANCE_PX = 1;

const getDraggableVerticalBoundsElement = (scrollable: HTMLElement): HTMLElement =>
    (scrollable.querySelector(GRID_ZONE_LAYOUT_SELECTOR) as HTMLElement | null) ?? scrollable;

/**
 * Whether `rect` intersects the carousel's visible, clickable region: same horizontal rule as the
 * top nav, plus vertical overlap with the zone grid inside the scrollable (fallback: wrapper rect).
 * The grid is used for height because the wrapper can report a shorter box than a multi-row layout
 * (e.g. spotlight second row). Top nav keeps using only {@link isRectIntersectingClickableHorizontalBand}.
 */
export const isRectIntersectingDraggableVisibleArea = (rect: DOMRectReadOnly, scrollable: HTMLElement): boolean => {
    if (!isRectIntersectingClickableHorizontalBand(rect, scrollable)) {
        return false;
    }
    const t = DRAGGABLE_VISIBLE_EDGE_TOLERANCE_PX;
    const { top, bottom } = getDraggableVerticalBoundsElement(scrollable).getBoundingClientRect();
    return rect.bottom > top - t && rect.top < bottom + t;
};
